import { Button } from "@/components/ui/Button";
import { useCallback, useEffect, useRef, useState } from 'react';
import { HlsVideo } from '@/features/playback/HlsVideo';
import { feedbackConversationAuthorityFingerprint } from '@/lib/api';
import type { ResourceMediaSelection, ResourceReadScope } from '@/lib/types';
import { createResourceMediaHandle } from '@/lib/resourceMedia';


/** One native video, still owned by the existing canvas callback ref. No blob
 * recording, alternate URL, bearer query or legacy-source fallback is admitted. */
export type ResourceMediaPlaybackProps = {
  scope: ResourceReadScope; selection: ResourceMediaSelection; active?: boolean;
  videoRef(el: HTMLVideoElement | null): void; controls?: boolean;
  onUnavailable?(): void; testId?: string;
};
export function ResourceMediaPlayback({scope,selection,active=true,videoRef,controls=false,onUnavailable,testId='resource-video'}:ResourceMediaPlaybackProps) {
  const [attempt,setAttempt]=useState(0);
  const retired=useRef(false);
  const key=JSON.stringify([scope,selection,feedbackConversationAuthorityFingerprint(),active,attempt]);
  const current=useRef({scope,selection,onUnavailable});current.current={scope,selection,onUnavailable};
  const video=useRef<HTMLVideoElement|null>(null);
  const media=useRef<ReturnType<typeof createResourceMediaHandle>|null>(null);
  const [state,setState]=useState<{key:string;url:string|null;error:boolean}>({key:'',url:null,error:false});
  const clear=useCallback(()=>{const el=video.current;if(el){el.pause();el.removeAttribute('src');el.load();}},[]);
  const ref=useCallback((el:HTMLVideoElement|null)=>{video.current=el;videoRef(el);},[videoRef]);
  useEffect(()=>{
    const selected=current.current;
    setState({key,url:null,error:false});
    if(!active)return;
    if(retired.current){setState({key,url:null,error:true});return;}
    let mounted=true;
    const handle=createResourceMediaHandle(selected.scope,selected.selection);media.current=handle;
    const unavailable=()=>{clear();if(mounted){retired.current=true;setState({key,url:null,error:true});current.current.onUnavailable?.();}};
    void (async()=>{
      try {
        const native=handle.cookieMediaUrl(unavailable);
        await handle.head();
        if(mounted&&handle.current())setState({key,url:native.url,error:false});
      } catch { if(mounted){clear();setState({key,url:null,error:true});} }
    })();
    return ()=>{mounted=false;if(media.current===handle)media.current=null;handle.dispose();clear();};
  },[key,active,clear]);
  const failed=()=>{
    clear();setState({key,url:null,error:true});
    // A browser codec/network error is not itself an authorization result.
    // Reauthorize through the same exact handle; a denial retires protected data.
    void media.current?.head().catch(()=>{});
  };
  if(!active)return null;
  const visible=state.key===key;
  if(!visible||!state.url)return <div className="absolute inset-0 flex flex-col gap-3 items-center justify-center text-paper/75" role="status">
    {visible&&state.error?'This exact recording is unavailable.':'Loading the selected recording…'}
    {visible&&state.error&&!retired.current&&<Button type="button" variant="outline" onClick={()=>setAttempt(value=>value+1)} onKeyDown={event=>event.stopPropagation()}>Retry recording</Button>}
  </div>;
  return <HlsVideo key={key} exactMp4Url={state.url} ref={ref} controls={controls}
    onUnavailable={failed} className="absolute inset-0 w-full h-full object-cover" data-testid={testId}/>;
}
